// TelcoTrack — Úřady: páruj kontakt podle subjectKey / orgType / názvu, ne první úřad v adresáři.

import { AUTHORITY_DOSS_CATALOG, type AuthorityDossCatalogEntry } from '../constants/authority-domain.constants';
import {
  susContactForRegion,
  susRegionKeyFromPlace,
} from '../constants/authority-overlay-identity.constants';

export const AUTHORITY_BUILDING_OFFICE_SUBJECTS = ['obec', 'jes', 'koordinovane_stanovisko'] as const;

/** Výzva / výjimka — catalog/apply je nesmí vysypat na každou stavbu. */
export const AUTHORITY_EXCEPTIONAL_SUBJECTS = [
  'khs',
  'pcr',
  'drazni_urad',
  'hzs',
  'cgs_chlu',
  'jes',
  'koordinovane_stanovisko',
  'mo_sem',
] as const;

export type AuthorityExceptionalSubject = (typeof AUTHORITY_EXCEPTIONAL_SUBJECTS)[number];

/** Typy, které se nesmí párrovat holým subjectKey — jen IČO / kraj. Zatím SÚS. */
export const AUTHORITY_ICO_TERRITORIAL_SUBJECTS = ['sus'] as const;

export type AuthorityIcoTerritorialSubject = (typeof AUTHORITY_ICO_TERRITORIAL_SUBJECTS)[number];

export interface AuthorityContactPairingInput {
  id?: string | null;
  name?: string | null;
  orgType?: string | null;
  subjectKey?: string | null;
  municipality?: string | null;
  region?: string | null;
  ico?: string | null;
  email?: string | null;
}

/** Místo stavby pro územní párování. Řetězec = obec (zpětná kompatibilita). */
export interface AuthorityContactPairingPlace {
  municipality?: string | null;
  region?: string | null;
  ico?: string | null;
}

export interface AuthorityContactPairingMatch {
  id: string | null;
  name: string | null;
  email: string | null;
}

const SUBJECT_NEEDLES: Record<string, { include: readonly string[]; exclude?: readonly string[] }> = {
  obec: {
    include: ['stavebni urad', 'stavební úřad', 'mestsky urad', 'městský úřad', 'obecni urad', 'obecní úřad', 'obec', 'mesto', 'město'],
    exclude: [
      'povod',
      'lesy',
      'aopk',
      'npu',
      'npú',
      'rsd',
      'řsd',
      'sus',
      'sús',
      'spu',
      'spú',
      'uzsvm',
      'úzsvm',
      'archeolog',
      'hzs',
      'hasic',
      'hasič',
      'khs',
      'hygien',
      'pcr',
      'pčr',
      'policie',
      'drazni',
      'drážní',
    ],
  },
  jes: {
    include: ['stavebni urad', 'stavební úřad', 'mestsky urad', 'městský úřad', 'obecni urad', 'obecní úřad'],
    exclude: [
      'povod',
      'lesy',
      'aopk',
      'npu',
      'npú',
      'rsd',
      'řsd',
      'sus',
      'sús',
      'spu',
      'spú',
      'uzsvm',
      'úzsvm',
      'archeolog',
      'hzs',
      'hasic',
      'hasič',
    ],
  },
  koordinovane_stanovisko: {
    include: ['stavebni urad', 'stavební úřad', 'mestsky urad', 'městský úřad', 'koordinovan'],
    exclude: ['povod', 'lesy', 'aopk', 'npu', 'rsd', 'řsd', 'hzs', 'hasic', 'hasič'],
  },
  hzs: { include: ['hzs', 'hasic', 'hasič', 'hasics'] },
  povodi: { include: ['povod'] },
  lesy_cr: { include: ['lesy', 'pupfl', 'lesni sprava', 'lesní správa'] },
  aopk: { include: ['aopk', 'agentura ochrany', 'chko', 'natura'] },
  npu: { include: ['npu', 'npú', 'pamatk', 'památ'] },
  rsd: { include: ['rsd', 'řsd', 'reditelstvi silnic', 'ředitelství silnic'] },
  sus: { include: ['sus', 'sús', 'sprava a udrzba silnic', 'správa a údržba silnic'] },
  spu: { include: ['spu', 'spú', 'statni pozemkovy', 'státní pozemkový'] },
  uzsvm: { include: ['uzsvm', 'úzsvm', 'zastupovani statu', 'zastupování státu'] },
  mo_sem: { include: ['ministerstvo obrany', 'mo-sem', 'mo – sem', 'mo sem'] },
  archeologicky_ustav: { include: ['archeolog'] },
  khs: { include: ['khs', 'hygien'] },
  pcr: { include: ['pcr', 'pčr', 'policie'] },
  drazni_urad: { include: ['drazni', 'drážní', 'zeleznic', 'železnic'] },
  cgs_chlu: { include: ['chlu', 'ložisk', 'lozisk', 'bansky', 'báňsk', 'obu'] },
};

export function isExceptionalCatalogSubject(subjectKey: string | null | undefined): boolean {
  return (AUTHORITY_EXCEPTIONAL_SUBJECTS as readonly string[]).includes((subjectKey ?? '').trim());
}

export function isIcoTerritorialSubject(subjectKey: string | null | undefined): boolean {
  return (AUTHORITY_ICO_TERRITORIAL_SUBJECTS as readonly string[]).includes((subjectKey ?? '').trim());
}

export function pairingPlaceFromArg(
  municipalityOrPlace?: string | null | AuthorityContactPairingPlace,
): AuthorityContactPairingPlace {
  if (municipalityOrPlace == null) return {};
  if (typeof municipalityOrPlace === 'string') return { municipality: municipalityOrPlace };
  return municipalityOrPlace;
}

/** Katalog / apply: SÚS dostane IČO z kraje stavby, obec zůstává pro SÚ. */
export function pairingPlaceForSubject(
  subjectKey: string,
  place: { municipality?: string | null; region?: string | null } = {},
): AuthorityContactPairingPlace {
  const municipality = place.municipality ?? null;
  const region = place.region ?? null;
  if (isIcoTerritorialSubject(subjectKey)) {
    return {
      municipality,
      region,
      ico: susContactForRegion(region)?.ico ?? null,
    };
  }
  return { municipality, region };
}

/** Katalog „všechny“ i výběr subjectKeys — výjimky (KHS, PČR, Drážní, HZS) se nevysypou. */
export function catalogSubjectsToApply(subjectKeys?: string[] | null): AuthorityDossCatalogEntry[] {
  const requested = subjectKeys?.length
    ? AUTHORITY_DOSS_CATALOG.filter((row) => subjectKeys.includes(row.subjectKey))
    : [...AUTHORITY_DOSS_CATALOG];
  return requested.filter((row) => !isExceptionalCatalogSubject(row.subjectKey));
}

export function isBuildingOfficeSubject(subjectKey: string | null | undefined): boolean {
  return (AUTHORITY_BUILDING_OFFICE_SUBJECTS as readonly string[]).includes((subjectKey ?? '').trim());
}

export function foldAuthorityContactText(value: string | null | undefined): string {
  return (value ?? '')
    .toLocaleLowerCase('cs')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Páruj úřad k povinnosti. Bez shody subjectKey / orgType / názvu vrací null — nikdy první kontakt v adresáři.
 * obec / jes / koordinované → SÚ jen při shodě obce (bez obce = prázdno, žádný fallback);
 * hzs → HZS; ostatní jen při shodě.
 */
export function matchAuthorityContactForSubject<T extends AuthorityContactPairingInput>(
  subjectKey: string | null | undefined,
  contacts: readonly T[],
  municipalityOrPlace?: string | null | AuthorityContactPairingPlace,
): T | null {
  const key = (subjectKey ?? '').trim();
  if (!key || !contacts.length) return null;

  const place = pairingPlaceFromArg(municipalityOrPlace);
  const mun = foldAuthorityContactText(place.municipality);
  const ranked = contacts
    .map((contact) => ({ contact, rank: rankContactForSubject(key, contact, mun, place) }))
    .filter((row) => row.rank > 0)
    .sort((a, b) => b.rank - a.rank);
  return ranked[0]?.contact ?? null;
}

export function pairedContactDisplay<T extends AuthorityContactPairingInput>(
  contact: T | null,
): AuthorityContactPairingMatch {
  if (!contact) return { id: null, name: null, email: null };
  return {
    id: (contact.id ?? '').trim() || null,
    name: (contact.name ?? '').trim() || null,
    email: (contact.email ?? '').trim() || null,
  };
}

function rankContactForSubject(
  subjectKey: string,
  contact: AuthorityContactPairingInput,
  municipalityFold: string,
  place: AuthorityContactPairingPlace,
): number {
  const contactKey = (contact.subjectKey ?? '').trim();
  const hay = foldAuthorityContactText(`${contact.orgType ?? ''} ${contact.name ?? ''}`);
  const spec = SUBJECT_NEEDLES[subjectKey];
  const exclude = spec?.exclude ?? [];
  if (exclude.some((needle) => hay.includes(foldAuthorityContactText(needle)))) return 0;

  let rank = 0;
  if (contactKey && contactKey === subjectKey) rank += 80;
  else if (contactKey && isBuildingOfficeSubject(subjectKey) && isBuildingOfficeSubject(contactKey)) rank += 70;
  else if (spec?.include.some((needle) => hay.includes(foldAuthorityContactText(needle)))) rank += 40;
  else return 0;

  if (isIcoTerritorialSubject(subjectKey)) {
    const expectedKey = susRegionKeyFromPlace({
      ico: place.ico,
      region: place.region,
    });
    const contactRegionKey = susRegionKeyFromPlace({
      ico: contact.ico,
      region: contact.region,
    });
    if (!expectedKey || !contactRegionKey || expectedKey !== contactRegionKey) return 0;
    rank += 25;
    return rank;
  }

  // SSÚ je úřad konkrétního ORP / obce — ne první řádek s tímto subjectKey.
  if (subjectKey === 'silnicni_spravni_urad') {
    if (!municipalityFold) return 0;
    const officePlace = foldAuthorityContactText(`${contact.municipality ?? ''} ${contact.name ?? ''}`);
    if (!officePlace.includes(municipalityFold)) return 0;
    rank += 25;
    return rank;
  }

  if (isBuildingOfficeSubject(subjectKey)) {
    if (!municipalityFold) return 0;
    const officePlace = foldAuthorityContactText(`${contact.municipality ?? ''} ${contact.name ?? ''}`);
    if (!officePlace.includes(municipalityFold)) return 0;
    rank += 15;
    return rank;
  }

  if (municipalityFold) {
    const namedPlace = foldAuthorityContactText(`${contact.municipality ?? ''} ${contact.name ?? ''}`);
    if (namedPlace.includes(municipalityFold)) rank += 15;
  }
  return rank;
}
